import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, Th, Td, adminInput } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const TYPES = ["Guest incident", "Injury", "Property damage", "Complaint", "Security", "Medical", "Policy violation", "Other"];
const STATUSES = ["open", "investigating", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

function NewIncident({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "Guest incident", description: "", location: "", priority: "normal", guest_name: "", witnesses: "", immediate_action: "", financial_impact: 0 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.description) { toast.error("Description is required."); return; }
    try { await api.post("/admin/incidents", f); toast.success("Incident filed."); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="new-incident-btn"><Plus size={15} /> File Incident</button></DialogTrigger>
      <DialogContent className="bg-surface border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">File Incident</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select className={adminInput} value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="incident-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <textarea className={adminInput} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="incident-description" />
          <div className="grid grid-cols-2 gap-3">
            <input className={adminInput} placeholder="Location" value={f.location} onChange={(e) => set("location", e.target.value)} />
            <input className={adminInput} placeholder="Guest name" value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={adminInput} value={f.priority} onChange={(e) => set("priority", e.target.value)}>{PRIORITIES.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}</select>
            <input className={adminInput} type="number" placeholder="Financial impact ($)" value={f.financial_impact} onChange={(e) => set("financial_impact", parseFloat(e.target.value) || 0)} />
          </div>
          <input className={adminInput} placeholder="Witnesses" value={f.witnesses} onChange={(e) => set("witnesses", e.target.value)} />
          <textarea className={adminInput} rows={2} placeholder="Immediate action taken" value={f.immediate_action} onChange={(e) => set("immediate_action", e.target.value)} />
          <button onClick={save} className="btn-gold w-full py-2.5 text-sm" data-testid="incident-save">File Incident</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminIncidents() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/incidents").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const update = async (id, body) => { try { await api.put(`/admin/incidents/${id}`, body); toast.success("Updated."); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <AdminHeading title="Incidents" subtitle="Confidential incident records. Management access only.">
        <NewIncident onDone={load} />
      </AdminHeading>
      <Panel>
        {items === null ? <Loading /> : items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center flex items-center justify-center gap-2"><AlertTriangle size={15} /> No incidents on record.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr><Th>Date</Th><Th>Type</Th><Th>Guest</Th><Th>Location</Th><Th>Priority</Th><Th>Status</Th><Th>Impact</Th></tr></thead>
              <tbody>{items.map((i) => (
                <tr key={i.id} data-testid={`incident-row-${i.id}`}>
                  <Td className="text-muted-foreground whitespace-nowrap">{fmtDate(i.occurred_at || i.created_at)}</Td>
                  <Td>{i.type}<span className="text-xs text-muted-foreground block max-w-[240px] truncate">{i.description}</span></Td>
                  <Td>{i.guest_name || "—"}{i.source === "concierge" && <span className="text-[10px] text-pool block">from concierge</span>}</Td>
                  <Td className="text-muted-foreground">{i.location || "—"}</Td>
                  <Td className="capitalize">{i.priority}</Td>
                  <Td><select className={adminInput} value={i.status} onChange={(e) => update(i.id, { status: e.target.value })} data-testid={`incident-status-${i.id}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Td>
                  <Td>{i.financial_impact ? `$${i.financial_impact}` : "—"}</Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
